import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { ToastService } from '../../../../shared/services/toast.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-coupon-recharge',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './coupon-recharge.component.html',
  styleUrl: './coupon-recharge.component.scss'
})
export class CouponRechargeComponent implements OnInit, OnDestroy {
  siteid: string | null = null;
  siteName: string = '';
  locations: any[] = [];
  locationid: string = '';
  serialNumber: string = '';
  isRecharging: boolean = false;

  private queryParamsSubs?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiCallsService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const rawSiteId = this.route.snapshot.queryParamMap.get('siteid') || localStorage.getItem('site_idLogin');
    if (rawSiteId) {
      try {
        this.siteid = atob(rawSiteId);
      } catch {
        this.siteid = rawSiteId;
      }
    }
    this.loadSiteName();
    this.loadLocations();

    this.queryParamsSubs = this.route.queryParams.subscribe((params: any) => {
      if (params['siteid']) {
        try {
          this.siteid = atob(params['siteid']);
        } catch {
          this.siteid = params['siteid'];
        }
        this.loadSiteName();
        this.locationid = '';
        this.loadLocations();
      }
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSubs?.unsubscribe();
  }

  /**
   * Resolves the human-readable site name from the 'siteList' stored in localStorage.
   * If not available in localStorage yet, it fetches the site list from the API and caches it.
   */
  private resolveSiteName(siteId: string | null): string {
    if (!siteId) return '';
    try {
      const stored = localStorage.getItem('siteList');
      if (stored) {
        const parsed = JSON.parse(stored);
        const list: any[] = Array.isArray(parsed) ? parsed : (parsed?.Data?.list || parsed?.list || []);
        const matched = list.find((s: any) => String(s.site_id) === String(siteId) || String(s.id) === String(siteId));
        if (matched?.site_name) return matched.site_name;
        if (matched?.facility_name) return matched.facility_name;
        if (matched?.name) return matched.name;
      }
    } catch {
      // ignore JSON errors
    }
    const directName = localStorage.getItem('site_name') || localStorage.getItem('SiteName');
    if (directName && directName !== 'undefined') return directName;
    return '';
  }

  private loadSiteName(): void {
    const name = this.resolveSiteName(this.siteid);
    if (name) {
      this.siteName = name;
      return;
    }

    // Fallback: fetch sitelist from API if not yet present in localStorage
    if (this.siteid) {
      this.apiService.getSiteList().subscribe({
        next: (res: any) => {
          const list = res?.Data?.list || (Array.isArray(res?.Data) ? res.Data : (Array.isArray(res) ? res : []));
          if (list && list.length > 0) {
            localStorage.setItem('siteList', JSON.stringify(list));
            this.siteName = this.resolveSiteName(this.siteid);
          }
        },
        error: () => {}
      });
    }
  }

  private loadLocations(): void {
    this.locations = [];
    if (!this.siteid) return;

    this.apiService.getLocationList(this.siteid).subscribe({
      next: (res: any) => {
        const list = res?.Data?.list || res?.Data || [];
        this.locations = Array.isArray(list) ? list : [];
      },
      error: () => {
        this.toast.showToast('Failed to fetch location list.', 'colored', 5000, 'warning');
      }
    });
  }

  onRechargeSubmit(): void {
    const trimmedSerial = (this.serialNumber || '').trim();

    if (!this.siteid) {
      this.toast.showToast('Please select a site from the header dropdown first.', 'colored', 4000, 'warning');
      return;
    }

    if (!trimmedSerial) {
      this.toast.showToast('Please enter a valid serial number.', 'colored', 4000, 'warning');
      return;
    }

    if (!this.locationid) {
      this.toast.showToast('Please select a location.', 'colored', 4000, 'warning');
      return;
    }

    Swal.fire({
      title: 'Confirm Recharge',
      html: `
        <div class="text-start">
          <p class="mb-1"><strong>Site:</strong> ${this.siteName || 'Selected Site'}</p>
          <p class="mb-1"><strong>Serial Number:</strong> <code>${trimmedSerial}</code></p>
          <p class="text-muted small mt-2 mb-0">Are you sure you want to recharge with this serial number?</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Recharge',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.isRecharging = true;
      const fd = new FormData();
      fd.append('site_id', this.siteid || '');
      fd.append('location_id', this.locationid);
      fd.append('coupon_id', trimmedSerial);

      // Endpoint: Coupon-Discard (recharge with coupon serial number)
      this.apiService.couponRecharge(fd).subscribe({
        next: (res: any) => {
          this.isRecharging = false;
          if (res?.Status === 'Error' || res?.status === false) {
            const errorMsg = res?.Message || res?.message || 'Recharge failed. Please check the serial number.';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
            Swal.fire({
              title: 'Recharge Failed',
              text: errorMsg,
              icon: 'error',
              confirmButtonText: 'OK',
              confirmButtonColor: '#d33'
            });
            return;
          }

          const successMsg = res?.Message || res?.message || 'Recharge successful!';
          this.toast.showToast(successMsg, 'colored', 5000, 'success');
          Swal.fire({
            title: 'Recharge Successful!',
            text: successMsg,
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#198754'
          });
          this.serialNumber = '';
        },
        error: (err: any) => {
          this.isRecharging = false;
          const errorMsg = err?.error?.Message || err?.error?.message || err?.message || 'An error occurred while processing the recharge.';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          Swal.fire({
            title: 'Recharge Error',
            text: errorMsg,
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#d33'
          });
        }
      });
    });
  }

  onReset(): void {
    this.serialNumber = '';
  }
}
